import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { BotAuthorizationDisplay } from '@components/BotAuthorizationDisplay/BotAuthorizationDisplay'
import { getDurationKeyFromSeconds } from '@components/DeveloperPortal/CreateBot/const'
import { ModalContainer } from '@components/Modals/ModalContainer'
import { popupToast } from '@components/Notifications/popupToast'
import { StandardToast } from '@components/Notifications/StandardToast'
import { SpaceIcon } from '@components/SpaceIcon'
import { Spinner } from '@components/Spinner'
import { useMutation } from '@tanstack/react-query'
import { Box, Button, FancyButton, Icon, IconButton, Pill, Stack, Text } from '@ui'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { Analytics } from 'hooks/useAnalytics'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { type GetSigner, WalletReady } from 'privy/WalletReady'
import { PATHS } from 'routes'
import {
    type BotData,
    type Channel,
    SignerUndefinedError,
    SpaceAddressFromSpaceId,
    type SpaceItem,
    useBotChannels,
    useInstallAppTransaction,
    useInstalledApps,
    useSpaceDataWithId,
    useTownsClient,
    useTownsContext,
} from 'use-towns-client'
import { isTownBanned } from 'utils'
import { type Address } from 'viem'

type InstallStep = 'list-permissions' | 'space-selection' | 'channel-selection'

export interface InstallBotOnSpaceDialogProps {
    isOpen: boolean
    onClose: () => void
    bot: BotData
}

type State = {
    spaceStreamId: string
    channelStreamIds: Set<string>
}

export const InstallBotOnSpaceDialog = ({ isOpen, onClose, bot }: InstallBotOnSpaceDialogProps) => {
    const navigate = useNavigate()

    const [currentStep, setCurrentStep] = useState<InstallStep>('list-permissions')
    const [state, setState] = useState<State>({
        spaceStreamId: '',
        channelStreamIds: new Set(),
    })
    const { spaceDapp, client } = useTownsClient()

    const spaceAddress = state.spaceStreamId
        ? SpaceAddressFromSpaceId(state.spaceStreamId)
        : undefined
    const { data: installedApps } = useInstalledApps(spaceAddress)

    const { installApp } = useInstallAppTransaction()
    const { data: aaAddress } = useMyAbstractAccountAddress()
    const { spaces } = useTownsContext()
    const allSpacesOwnedByUser = useMemo(
        () =>
            spaces.filter((space) => {
                if (space.owner !== aaAddress) {
                    return false
                }
                if (isTownBanned(space.address)) {
                    return false
                }
                return true
            }),
        [spaces, aaAddress],
    )

    const { mutate: installBot, isPending: isInstalling } = useMutation({
        mutationFn: async (getSigner: GetSigner) => {
            if (!state.spaceStreamId || state.channelStreamIds.size === 0) {
                return
            }
            if (!spaceDapp) {
                throw new Error('SpaceDapp not found')
            }
            if (!client) {
                throw new Error('Client not found')
            }
            const signer = await getSigner()
            if (!signer) {
                throw new SignerUndefinedError()
            }
            const space = spaceDapp.getSpace(state.spaceStreamId)

            if (!space) {
                throw new Error('Space not found')
            }
            const isBotInstalled = installedApps?.has(bot.clientAddress)
            if (!isBotInstalled) {
                const promise = new Promise<void>((resolve, reject) =>
                    installApp(
                        {
                            signer,
                            appAddress: bot.appAddress,
                            spaceAddress: SpaceAddressFromSpaceId(state.spaceStreamId) as Address,
                            price: bot.installPrice,
                            clientAddress: bot.clientAddress,
                            appId: bot.latestAppId,
                        },
                        {
                            onSuccess: () => {
                                Analytics.getInstance().track('submit bot fee', {
                                    botName: bot.metadata?.username,
                                    botId: bot.clientAddress,
                                    botCreator: bot.owner,
                                    pricingModule: bot.installPrice === 0n ? 'free' : 'fixed',
                                    priceInWei: bot.installPrice.toString(),
                                })

                                resolve()
                            },
                            onError: (error) => {
                                reject(error)
                            },
                        },
                    ),
                )
                await promise
            }
        },
        onSuccess: async () => {
            if (!client) {
                throw new Error('Client not found')
            }
            await client.joinUser(state.spaceStreamId, bot.clientAddress)
            await Promise.all(
                Array.from(state.channelStreamIds).map((channelId) =>
                    client.joinUser(channelId, bot.clientAddress),
                ),
            )

            Analytics.getInstance().track('installed bot', {
                botName: bot.metadata?.username,
                botId: bot.clientAddress,
                botCreator: bot.owner,
                duration: getDurationKeyFromSeconds(bot.accessDuration).toLowerCase(),
                priceInWei: bot.installPrice.toString(),
            })

            popupToast(({ toast }) => (
                <StandardToast.Success
                    toast={toast}
                    message="Bot installed successfully!"
                    subMessage="The bot has been added to your selected channels."
                />
            ))

            const installedSpaceAddress = SpaceAddressFromSpaceId(state.spaceStreamId)
            handleClose()
            navigate(`/${PATHS.SPACES}/${installedSpaceAddress}`)
        },
        onError: (error) => {
            console.error('Installation failed:', error)

            popupToast(({ toast }) => (
                <StandardToast.Error
                    toast={toast}
                    message="Failed to install bot"
                    subMessage="Please try again or contact support if the problem persists."
                />
            ))
        },
    })

    const handleClose = () => {
        setState({ spaceStreamId: '', channelStreamIds: new Set() })
        setCurrentStep('list-permissions')
        onClose()
    }

    const handleSpaceSelect = useCallback((spaceStreamId: string) => {
        setState((prev) => ({ ...prev, spaceStreamId, channelStreamIds: new Set() }))
        setCurrentStep('channel-selection')
    }, [])

    const handleChannelToggle = useCallback((channelId: string, checked: boolean) => {
        setState((prev) => {
            const newSet = new Set(prev.channelStreamIds)
            if (checked) {
                newSet.add(channelId)
            } else {
                newSet.delete(channelId)
            }
            return { ...prev, channelStreamIds: newSet }
        })
    }, [])

    const handleSelectAllChannels = useCallback((channelStreamIds: Set<string>) => {
        setState((prev) => ({ ...prev, channelStreamIds }))
    }, [])

    const handleBackToSpaceSelection = useCallback(() => {
        setCurrentStep('space-selection')
    }, [])

    const handleContinueFromPermissions = useCallback(() => {
        setCurrentStep('space-selection')
    }, [])

    const handleBackToPermissions = useCallback(() => {
        setCurrentStep('list-permissions')
        setState({ spaceStreamId: '', channelStreamIds: new Set() })
    }, [])

    if (!isOpen) {
        return null
    }

    return (
        <ModalContainer padding="none" disableBackgroundClick={isInstalling} onHide={handleClose}>
            <Stack gap="sm">
                <Stack
                    horizontal
                    alignItems="center"
                    justifyContent="center"
                    padding="lg"
                    position="relative"
                >
                    <Stack horizontal gap alignItems="center">
                        {(currentStep === 'channel-selection' ||
                            currentStep === 'space-selection') && (
                            <IconButton
                                position="absolute"
                                style={{ left: '16px' }}
                                icon="arrowLeft"
                                size="square_sm"
                                disabled={isInstalling}
                                onClick={
                                    currentStep === 'channel-selection'
                                        ? handleBackToSpaceSelection
                                        : handleBackToPermissions
                                }
                            />
                        )}
                    </Stack>
                    <Text fontWeight="strong" color="default">
                        {currentStep === 'list-permissions'
                            ? 'Authorize Bot'
                            : currentStep === 'space-selection'
                              ? 'Select a space where you want to install the bot'
                              : 'Select channels for the bot to join'}
                    </Text>
                    <IconButton
                        position="absolute"
                        style={{ right: '16px' }}
                        icon="close"
                        size="square_sm"
                        disabled={isInstalling}
                        onClick={handleClose}
                    />
                </Stack>
            </Stack>

            <Box padding="lg" minHeight="400" maxHeight="500" overflow="auto">
                {currentStep === 'list-permissions' && <BotAuthorizationDisplay bot={bot} />}

                {currentStep === 'space-selection' &&
                    (!allSpacesOwnedByUser ? (
                        <Box centerContent width="100%" height="100%" padding="lg">
                            <Spinner />
                        </Box>
                    ) : (
                        <SpaceSelectionStep
                            spaces={allSpacesOwnedByUser}
                            selectedSpaceStreamId={state.spaceStreamId}
                            bot={bot}
                            onSpaceSelect={handleSpaceSelect}
                            onClose={handleClose}
                        />
                    ))}

                {currentStep === 'channel-selection' && (
                    <ChannelSelectionStep
                        spaceStreamId={state.spaceStreamId}
                        selectedChannelIds={state.channelStreamIds}
                        isInstalling={isInstalling}
                        bot={bot}
                        onChannelToggle={handleChannelToggle}
                        onSelectAllChannels={handleSelectAllChannels}
                    />
                )}
            </Box>

            {currentStep === 'list-permissions' && (
                <Box padding="lg" paddingTop="md" borderTop="default">
                    <Stack horizontal justifyContent="end" gap="sm">
                        <Button tone="level2" size="button_md" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button
                            tone="cta1"
                            size="button_md"
                            onClick={handleContinueFromPermissions}
                        >
                            Continue
                        </Button>
                    </Stack>
                </Box>
            )}
            {currentStep === 'space-selection' && (
                <Box padding="lg" paddingTop="md" borderTop="default">
                    <Stack horizontal justifyContent="end">
                        <Button tone="level2" size="button_md" onClick={handleClose}>
                            Cancel
                        </Button>
                    </Stack>
                </Box>
            )}
            {currentStep === 'channel-selection' && (
                <Box paddingTop="md" padding="lg" borderTop="default">
                    <Stack horizontal justifyContent="end">
                        <WalletReady>
                            {({ getSigner }) => (
                                <FancyButton
                                    cta={state.channelStreamIds.size > 0 && !isInstalling}
                                    disabled={state.channelStreamIds.size === 0 || isInstalling}
                                    spinner={isInstalling}
                                    onClick={() => installBot(getSigner)}
                                >
                                    {isInstalling ? 'Installing...' : 'Install Bot'}
                                </FancyButton>
                            )}
                        </WalletReady>
                    </Stack>
                </Box>
            )}
        </ModalContainer>
    )
}

const SpaceSelectionStep = ({
    spaces,
    selectedSpaceStreamId,
    bot,
    onSpaceSelect,
    onClose,
}: {
    spaces: SpaceItem[]
    selectedSpaceStreamId: string
    bot: BotData
    onSpaceSelect: (spaceId: string) => void
    onClose: () => void
}) => {
    const navigate = useNavigate()
    const { enableBotDm } = useFeatureFlags()

    const handleStartDM = useCallback(() => {
        onClose()
        navigate(`/${PATHS.MESSAGES}/new?to=${bot.clientAddress}&appAddress=${bot.appAddress}`)
    }, [navigate, bot.clientAddress, bot.appAddress, onClose])

    return (
        <>
            <Stack gap="sm">
                {enableBotDm && (
                    <Stack
                        horizontal
                        hoverable
                        alignItems="center"
                        justifyContent="spaceBetween"
                        padding="sm"
                        rounded="sm"
                        cursor="pointer"
                        background="level1"
                        onClick={handleStartDM}
                    >
                        <Stack horizontal gap alignItems="center">
                            <Box
                                centerContent
                                width="x4"
                                minWidth="x4"
                                aspectRatio="1/1"
                                background="level2"
                                rounded="full"
                            >
                                <Icon type="message" size="square_sm" />
                            </Box>
                            <Text fontWeight="strong" color="default">
                                Start a DM instead
                            </Text>
                        </Stack>
                        <Icon type="arrowRight" size="square_sm" />
                    </Stack>
                )}
                {spaces.map((space) => (
                    <SpaceOption
                        key={space.streamId}
                        space={space}
                        bot={bot}
                        selected={selectedSpaceStreamId === space.streamId}
                        onSelect={() => onSpaceSelect(space.streamId)}
                    />
                ))}
            </Stack>
            {spaces.length === 0 && (
                <Box centerContent padding="lg">
                    <Stack alignItems="center" gap="md">
                        <Text color="gray2">To install a bot, you must be a Town owner</Text>
                        <FancyButton
                            cta
                            borderRadius="full"
                            onClick={() => navigate(`/${PATHS.SPACES}/new`)}
                        >
                            Create a Town
                        </FancyButton>
                    </Stack>
                </Box>
            )}
        </>
    )
}

const ChannelSelectionStep = ({
    spaceStreamId,
    selectedChannelIds,
    isInstalling,
    bot,
    onChannelToggle,
    onSelectAllChannels,
}: {
    spaceStreamId: string
    selectedChannelIds: Set<string>
    isInstalling: boolean
    bot: BotData
    onChannelToggle: (channelId: string, checked: boolean) => void
    onSelectAllChannels: (channelIds: Set<string>) => void
}) => {
    const spaceData = useSpaceDataWithId(spaceStreamId)

    const channels = useMemo(() => {
        if (!spaceData?.channelGroups) {
            return []
        }
        return spaceData.channelGroups
            .flatMap((group) => group.channels)
            .filter((channel) => !channel.disabled)
    }, [spaceData])

    const channelIds = useMemo(() => channels.map((c) => c.id), [channels])
    const botChannels = useBotChannels(channelIds, bot.clientAddress)

    const availableChannels = useMemo(
        () => channels?.filter((channel) => !botChannels.includes(channel.id)) || [],
        [channels, botChannels],
    )
    const isAllChannelsSelected =
        availableChannels.length > 0 && selectedChannelIds.size === availableChannels.length

    const isBotInAllChannels = useMemo(() => {
        return channels.length > 0 && botChannels && botChannels.length === channels.length
    }, [channels.length, botChannels])

    const handleSelectAllChannels = useCallback(
        (checked: boolean) => {
            if (checked) {
                onSelectAllChannels(new Set(availableChannels.map((channel) => channel.id)))
            } else {
                onSelectAllChannels(new Set())
            }
        },
        [availableChannels, onSelectAllChannels],
    )

    return (
        <Stack gap="lg">
            <Text fontWeight="strong" color="default">
                Channels in {spaceData?.name}
            </Text>

            {channels && channels.length > 0 && (
                <>
                    <Stack horizontal alignItems="center" justifyContent="spaceBetween">
                        <Text color="gray2">Select channels for the bot to join</Text>
                        <Stack
                            horizontal
                            alignItems="center"
                            gap="sm"
                            cursor={isBotInAllChannels ? 'default' : 'pointer'}
                            onClick={
                                isBotInAllChannels
                                    ? undefined
                                    : () => handleSelectAllChannels(!isAllChannelsSelected)
                            }
                        >
                            <Box
                                centerContent
                                width="x2"
                                height="x2"
                                rounded="full"
                                background={
                                    isAllChannelsSelected || isBotInAllChannels ? 'cta1' : 'level3'
                                }
                                border={
                                    isAllChannelsSelected || isBotInAllChannels
                                        ? undefined
                                        : 'default'
                                }
                                opacity={isBotInAllChannels ? '0.5' : undefined}
                            >
                                {(isAllChannelsSelected || isBotInAllChannels) && (
                                    <Icon type="check" size="square_xs" />
                                )}
                            </Box>
                            <Text color={isBotInAllChannels ? 'gray2' : 'default'}>
                                All channels
                            </Text>
                        </Stack>
                    </Stack>

                    {isBotInAllChannels && (
                        <Box padding="md" background="level2" rounded="sm">
                            <Text color="gray2" fontSize="sm">
                                Bot is already installed in all channels of this space
                            </Text>
                        </Box>
                    )}

                    <Stack gap="sm">
                        {channels.map((channel) => (
                            <ChannelOption
                                key={channel.id}
                                channel={channel}
                                selected={
                                    selectedChannelIds.has(channel.id) ||
                                    botChannels?.includes(channel.id) ||
                                    false
                                }
                                disabled={isInstalling || botChannels?.includes(channel.id)}
                                isBotInstalled={botChannels?.includes(channel.id) || false}
                                onToggle={(checked) => onChannelToggle(channel.id, checked)}
                            />
                        ))}
                    </Stack>
                </>
            )}

            {channels && channels.length === 0 && (
                <Box centerContent padding="lg">
                    <Text color="gray2">This space doesn&apos;t have any channels yet.</Text>
                </Box>
            )}
        </Stack>
    )
}

const SpaceOption = ({
    space,
    bot,
    selected,
    onSelect,
}: {
    space: SpaceItem
    bot: BotData
    selected: boolean
    onSelect: () => void
}) => {
    const spaceAddress = SpaceAddressFromSpaceId(space.streamId)
    const { data: installedApps } = useInstalledApps(spaceAddress)
    const isBotInstalled = installedApps?.has(bot.clientAddress)

    return (
        <Stack
            horizontal
            hoverable
            alignItems="center"
            justifyContent="spaceBetween"
            padding="sm"
            rounded="sm"
            cursor="pointer"
            background={selected ? 'level2' : 'level1'}
            border={selected ? 'default' : undefined}
            onClick={onSelect}
        >
            <Stack horizontal gap alignItems="center">
                <SpaceIcon
                    width="x4"
                    minWidth="x4"
                    aspectRatio="1/1"
                    background="level1"
                    rounded="xs"
                    variant="thumbnail100"
                    spaceId={space.streamId}
                    firstLetterOfSpaceName={space.name[0]}
                    fadeIn={false}
                />
                <Text fontWeight="strong" color="default">
                    {space.name}
                </Text>
            </Stack>
            <Stack horizontal alignItems="center" gap="sm">
                {isBotInstalled && <Pill color="cta1">Installed</Pill>}
                <Icon type="arrowRight" size="square_sm" />
            </Stack>
        </Stack>
    )
}

const ChannelOption = ({
    channel,
    selected,
    disabled = false,
    isBotInstalled = false,
    onToggle,
}: {
    channel: Channel
    selected: boolean
    disabled?: boolean
    isBotInstalled?: boolean
    onToggle: (checked: boolean) => void
}) => {
    return (
        <Stack
            horizontal
            alignItems="center"
            gap="sm"
            cursor={disabled ? 'default' : 'pointer'}
            onClick={disabled ? undefined : () => onToggle(!selected)}
        >
            <Box
                centerContent
                width="x2"
                height="x2"
                rounded="full"
                background={selected ? 'cta1' : 'level3'}
                border={selected ? undefined : 'default'}
                opacity={disabled ? '0.5' : undefined}
            >
                {selected && <Icon type="check" size="square_xs" />}
            </Box>
            <Stack horizontal gap="xs" alignItems="center">
                <Text color={disabled ? 'gray2' : 'default'}>#{channel.label}</Text>
                {isBotInstalled && (
                    <Text color="gray2" fontSize="xs">
                        (Bot already installed)
                    </Text>
                )}
            </Stack>
        </Stack>
    )
}
